/**
 * Semantic colour roles (prompt.md §40 `design-tokens`, §44 Android token sharing).
 *
 * WHY this module exists: the web UI, the Tauri shell and the Android client are
 * built with different rendering stacks, but they must *look like one product*
 * and, more importantly, must be able to explain a colour change in one place.
 *
 * So the unit of currency here is a **role** (`danger`, `textMuted`), never a
 * palette slot (`red500`). A role carries a value for each supported scheme;
 * no client is allowed to invent a hex value on its own.
 *
 * The palette is the Dayvise brand: a jetblack canvas and one loud coral lifted
 * from the outline of the logo mark. Everything else is tonality — steps of the
 * neutral ramp — plus a pastel accent family used only for decoration. Coral is
 * reserved for the single most important action on a screen; when everything is
 * accented, nothing is.
 */

/** Colour schemes every platform must be able to render. */
export const COLOR_SCHEMES = ["light", "dark"] as const;

export type ColorScheme = (typeof COLOR_SCHEMES)[number];

export const COLOR_ROLES = [
  "canvas",
  "surface",
  "raised",
  "surfaceHover",
  "border",
  "textPrimary",
  "textMuted",
  "accent",
  "accentHover",
  "accentPressed",
  "onAccent",
  "onAccentStrong",
  "accentSurface",
  "contrastSurface",
  "contrastInk",
  "danger",
  "success",
  "warning",
  "overlay",
  "accentCoral",
  "accentSky",
  "accentLime",
  "accentMint",
] as const;

export type ColorRole = (typeof COLOR_ROLES)[number];

export interface ColorToken {
  /** Value for the light scheme. */
  readonly light: string;
  /** Value for the dark scheme. */
  readonly dark: string;
  /** What the role is for, so a client picks the right one without guessing. */
  readonly description: string;
}

/**
 * The canvas → surface → raised ramp expresses elevation, not just colour:
 * every additional layer is one step closer to the viewer's attention. In the
 * dark scheme that means *lighter*; in the light scheme, *whiter*.
 */
export const COLORS: Readonly<Record<ColorRole, ColorToken>> = {
  canvas: {
    light: "#ffffff",
    // Jetblack, not pure black: #050506 keeps large flat areas from reading as
    // dead space and stops hairlines from banding against the background.
    dark: "#050506",
    description: "application background, behind every panel",
  },
  surface: {
    light: "#f5f5f7",
    dark: "#0a0a0c",
    description: "default panel or section background",
  },
  raised: {
    light: "#fbfbfc",
    dark: "#101012",
    description: "elevated container: cards, menus, dialogs, sheets",
  },
  surfaceHover: {
    light: "#f0f0f2",
    dark: "#1a1a1d",
    description: "hover step for a neutral surface; one step up the ramp, never a hue change",
  },
  border: {
    light: "#e5e5e9",
    dark: "#28282d",
    description: "dividers, field outlines and container edges",
  },
  textPrimary: {
    light: "#09090b",
    dark: "#f7f7f8",
    description: "primary reading text",
  },
  textMuted: {
    light: "#52525b",
    dark: "#a1a1aa",
    // Light mode is the trap: a greyer muted passes on large text and fails on
    // small, so this step is held at 7.7:1 against white and never lightened for
    // a "selected" or "inactive" state.
    description: "secondary text: timestamps, hints, metadata",
  },
  accent: {
    light: "#e0003a",
    dark: "#ff1f49",
    description: "the brand coral: primary action, selection and focus colour",
  },
  accentHover: {
    light: "#b3002e",
    dark: "#f21c45",
    // Hover moves *darker*, never toward a pastel. A hover that washes out reads
    // as disabled, so no interaction state is ever lighter than rest.
    description: "hover step for an accent-filled control; one step darker than accent",
  },
  accentPressed: {
    light: "#8f0023",
    dark: "#cc193d",
    description: "pressed step for an accent-filled control; one step darker than accentHover",
  },
  onAccent: {
    light: "#ffffff",
    dark: "#0a0a0c",
    description: "text and icons drawn on top of the accent colour",
  },
  onAccentStrong: {
    light: "#ffffff",
    dark: "#f7f7f8",
    // Pressing darkens the accent past the point where a near-black label can
    // hold 4.5:1, so the pressed label is the light one in both schemes.
    description: "label on a pressed accent fill, where the fill is too dark for onAccent",
  },
  accentSurface: {
    light: "#e0003a",
    // The accent toned to the pastel level of the accent family, used as a *fill*
    // behind onAccent text. The vivid accent stays for borders, dots and glows.
    dark: "#ffb3c1",
    description: "accent-tinted fill for pills, chips and eyebrows carrying onAccent text",
  },
  contrastSurface: {
    light: "#09090b",
    dark: "#f6f6f7",
    description: "neutral high-contrast fill: the default call-to-action before the accent",
  },
  contrastInk: {
    light: "#ffffff",
    dark: "#080809",
    description: "text and icons on contrastSurface",
  },
  danger: {
    light: "#c0262b",
    dark: "#ff6b6b",
    description: "destructive actions, failures and terminal run errors",
  },
  success: {
    light: "#127f4f",
    dark: "#4ade80",
    description: "completion and healthy state",
  },
  warning: {
    light: "#a16207",
    dark: "#fbbf24",
    description: "attention needed: paused runs, quota warnings, pending approval",
  },
  overlay: {
    light: "rgba(16, 24, 40, 0.48)",
    dark: "rgba(0, 0, 0, 0.6)",
    description: "scrim behind modal surfaces",
  },
  /**
   * The pastel accent family. Decorative only: these carry no text of their own
   * and never signal state — they cycle across card edges, charts and chips so a
   * grid of cards can be told apart without introducing a second brand colour.
   * Light-scheme values are one step deeper, because a pastel tuned for jetblack
   * disappears on white.
   */
  accentCoral: {
    light: "#e8663f",
    dark: "#ffad98",
    description: "decorative accent: coral, the family's anchor hue",
  },
  accentSky: {
    light: "#3f9fe0",
    dark: "#a8d9ff",
    description: "decorative accent: sky",
  },
  accentLime: {
    light: "#8fbf1f",
    dark: "#d7f77a",
    description: "decorative accent: lime",
  },
  accentMint: {
    light: "#2fa06b",
    dark: "#aef2d0",
    description: "decorative accent: mint",
  },
};

/** Human labels for roles, used by badges and diagnostics screens. */
export const COLOR_ROLE_LABELS: Readonly<Record<ColorRole, string>> = {
  canvas: "Canvas",
  surface: "Surface",
  raised: "Raised",
  surfaceHover: "Surface hover",
  border: "Border",
  textPrimary: "Text",
  textMuted: "Muted text",
  accent: "Accent",
  accentHover: "Accent hover",
  accentPressed: "Accent pressed",
  onAccent: "On accent",
  onAccentStrong: "On accent (pressed)",
  accentSurface: "Accent surface",
  contrastSurface: "Contrast surface",
  contrastInk: "On contrast surface",
  danger: "Danger",
  success: "Success",
  warning: "Warning",
  overlay: "Overlay",
  accentCoral: "Accent coral",
  accentSky: "Accent sky",
  accentLime: "Accent lime",
  accentMint: "Accent mint",
};

export function isColorRole(value: string): value is ColorRole {
  return (COLOR_ROLES as readonly string[]).includes(value);
}

/** Resolve a role for a scheme. Throws on an unknown role rather than guessing. */
export function resolveColor(role: ColorRole, scheme: ColorScheme): string {
  const token = COLORS[role];
  if (!token) throw new TypeError(`unknown colour role "${role}"`);
  return token[scheme];
}
